import { useTranslation } from "next-i18next";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import Accordion from "@atoms/Accordion";
import { IMG_DATA_404 } from "@config/images";
import DynamicImage from "@ds/components/DynamicImage";
import Text from "@ds/components/Text";
import useResponsive from "@helpers/useResponsive";
import color from "@definitions/mui/color";
import type { IFaqData, IListFaqProps } from "../../interface";
import useStyles from "../../styles";

const ListFaq: React.FC<IListFaqProps> = (props) => {
  const { dataFaqList, search } = props;
  const classes = useStyles();
  const { t } = useTranslation("faq");
  const isData = dataFaqList?.length !== 0;
  const isNotFound = !dataFaqList?.length;
  const isMD = useResponsive("md");

  const _renderSearchShow = () => {
    return (
      search && (
        <Box ml={1}>
          <Text color="general-mid">
            {t("result")}&nbsp;
            {`"${search}"`}
          </Text>
        </Box>
      )
    );
  };

  const _renderEmpty = () => {
    return (
      <>
        {isNotFound && (
          <Box className={classes?.wrapper} mt={3} mb={3}>
            <Box width={250}>
              <DynamicImage src={IMG_DATA_404} alt="search_not_found" width={250} height={209} />
            </Box>
            <Box mb="10px" mt="10px">
              <Text weight="700" sx={{ fontSize: "20px" }}>
                {t("message")}
              </Text>
            </Box>
            <Text color="general-mid">{t("description")}</Text>
          </Box>
        )}
      </>
    );
  };

  const _renderBody = () => {
    return (
      <>
        {isMD && !search && (
          <Text variant="subtitle1" weight="700" paragraph sx={{ lineHeight: 1 }}>
            {t('topics.subtitle')}
          </Text>
        )}
        {isData &&
          dataFaqList?.map((el: IFaqData, idx: number) => (
            <Accordion key={idx} sx={{ marginRight: isMD ? 0 : 1, marginLeft: isMD ? 0 : 1 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel-header"
              >
                <Text
                  variant="subtitle2"
                  color="general-main"
                  weight="700"
                  sx={{ fontSize: "14px" }}
                >
                  {el.title}
                </Text>
              </AccordionSummary>
              <AccordionDetails>
                <Text variant="body2" color="general-mid" sx={{ fontSize: "14px" }}>
                  <Box
                    color={isMD ? color.general.main : undefined}
                    sx={{
                      ...(isMD && {
                        fontSize: "12px",
                        fontWeight: "400",
                        lineHeight: "18px",
                        "& > p": {
                          margin: 0,
                          "& + p": {
                            marginBottom: "16px",
                          },
                        },
                      }),
                    }}
                    dangerouslySetInnerHTML={{ __html: el.descriptionStyle }}
                  ></Box>
                </Text>
                {el?.image.length > 0 && (
                  <ImageList style={{ display: "flex", flex: "wrap", gap: "8px" }}>
                    {el?.image.map((item: string, idx: number) => (
                      <ImageListItem key={idx}>
                        <img
                          src={item}
                          alt="items"
                          onClick={() => window.open(item)}
                          style={{ cursor: "pointer", objectFit: "contain" }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
      </>
    );
  };

  return (
    <>
      {_renderSearchShow()}
      {_renderEmpty()}
      {_renderBody()}
    </>
  );
};

export default ListFaq;
